import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Badge, Button, InputText, Typography, Card } from "@/deps/ui-library";
import { useSupportTickets } from "@/api/queries";
import { EmptyState } from "@/components/EmptyState";
import { SegmentControl } from "@/components/SegmentControl";
import { formatDateTime } from "@/lib/dates";
import { HelpGate } from "./HelpGate";
import { ticketStatusLabel, ticketStatusScheme } from "./supportLabels";

type Tab = "active" | "history";

/** The organisation's tickets with Rigo's support team: the live ones, or the closed history. */
export function SupportTicketsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("active");
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");

  // Asked a quarter-second after the typing stops, not on every key.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(typed.trim()), 250);
    return () => clearTimeout(handle);
  }, [typed]);

  const tickets = useSupportTickets({ history: tab === "history", ...(search ? { search } : {}) });

  return (
    <HelpGate section="tickets">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentControl
          label="Which tickets"
          value={tab}
          onChange={setTab}
          segments={[
            { value: "active", label: "Active" },
            { value: "history", label: "History" },
          ]}
        />
        <div className="flex items-center gap-2">
          <div className="w-64">
            <InputText.FilterSearch
              type="search"
              aria-label="Search tickets"
              placeholder="Search by title, number or area"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              onClear={() => setTyped("")}
            />
          </div>
          <Button
            colorScheme="primary"
            leftIcon="Plus"
            onClick={() => void navigate({ to: "/help/tickets/new" })}
          >
            New ticket
          </Button>
        </div>
      </div>

      {tickets.isError ? (
        <EmptyState
          icon="TriangleAlert"
          title="Your tickets could not be loaded"
          description={(tickets.error as Error).message}
        />
      ) : null}

      {tickets.data?.length === 0 ? (
        <EmptyState
          icon="Inbox"
          title={tab === "active" ? "No open tickets" : "No closed tickets yet"}
          description="Tickets your organisation raises with Rigo's support team appear here, with every reply."
        />
      ) : null}

      {tickets.data && tickets.data.length > 0 ? (
        <Card size="none" className="overflow-hidden">
          <ul aria-label="Tickets" className="flex flex-col">
            {tickets.data.map((ticket) => (
              <li key={ticket.id} className="border-border border-b last:border-0">
                <Link
                  to="/help/tickets/$id"
                  params={{ id: ticket.id }}
                  className="hover:bg-muted focus-visible:ring-ring flex items-center gap-3 px-4 py-2.5 no-underline focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                >
                  <span className="num text-foreground w-[76px] shrink-0 text-xs">
                    {ticket.ticketNo}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-foreground block truncate text-sm">
                      {ticket.title}
                    </span>
                    <Typography.Text as="span" variant="small" muted className="block">
                      {ticket.category} · raised by {ticket.raisedByName ?? "—"} · updated{" "}
                      {formatDateTime(ticket.lastActivityAt)}
                    </Typography.Text>
                  </span>
                  <Badge variant="soft" colorScheme={ticketStatusScheme(ticket.status)} size="sm">
                    {ticketStatusLabel(ticket.status)}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </HelpGate>
  );
}
